/**
 * The ⋯ on a row or a page: "Archive …" on an active one (a confirm first),
 * "Restore" on an archived one (none). Nothing at all for people who may not
 * change it.
 */

import { useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { Column } from "@/components/ui/data-table";
import { ArchiveDialog } from "./archive-dialog";
import { useRestore } from "./hooks";
import { KINDS, isArchived, type Archivable, type ArchiveKind } from "./kinds";
import { useMayArchive } from "./may";
import { RowMenu } from "./row-menu";

export interface ArchiveRow extends Archivable {
  id: string;
  name: string;
  created_by?: string | null;
}

export function ArchiveMenu({
  kind,
  row,
  size,
  onArchived,
  dialog,
}: {
  kind: ArchiveKind;
  row: ArchiveRow;
  size?: "sm" | "md" | undefined;
  onArchived?: (() => void) | undefined;
  /** Replaces the plain confirm, e.g. an experiment that may still be running. */
  dialog?: ((close: () => void) => ReactNode) | undefined;
}) {
  const [confirming, setConfirming] = useState(false);
  const may = useMayArchive(kind);
  const restore = useRestore(kind);
  if (!may(row)) return null;
  const { noun } = KINDS[kind];
  const close = () => setConfirming(false);
  return (
    <>
      <RowMenu
        label={`Actions for ${row.name}`}
        size={size}
        items={[
          isArchived(row)
            ? { label: "Restore", onSelect: () => void restore(row) }
            : { label: `Archive ${noun}…`, onSelect: () => setConfirming(true) },
        ]}
      />
      {/* Out of the table cell, so the dialog doesn't inherit its alignment. */}
      {confirming &&
        createPortal(
          dialog?.(close) ?? (
            <ArchiveDialog kind={kind} target={row} onClose={close} onArchived={onArchived} />
          ),
          document.body,
        )}
    </>
  );
}

/** The last column of a list: the row's ⋯. */
export const archiveColumn = <T extends ArchiveRow>(kind: ArchiveKind): Column<T> => ({
  header: <span className="sr-only">Actions</span>,
  // Pinned right: on a phone the table scrolls sideways and the ⋯ stays in view.
  headClass: "pin-end w-12",
  className: "pin-end text-right",
  isolated: true,
  cell: (row) => <ArchiveMenu kind={kind} row={row} />,
});
